<template>
  <div class="page">
    <div class="page-hero">
      <div>
        <h2>Webhooks</h2>
        <p>Configuración de endpoints para eventos del sistema.</p>
      </div>
      <v-btn color="primary" small @click="openForm()">+ Nuevo Webhook</v-btn>
    </div>

    <v-card outlined class="page-card">
      <v-card-text>
        <v-data-table :headers="headers" :items="rows" :loading="loading" dense>
          <template v-slot:item.isActive="{ item }">
            <v-chip x-small :color="item.isActive ? 'success' : 'grey'" dark label>{{ item.isActive ? "Activo" : "Inactivo" }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn text small color="primary" @click="openForm(item)">Editar</v-btn>
            <v-btn text small color="info" @click="showLogs(item)">Logs</v-btn>
            <v-btn text small color="error" @click="remove(item)">Eliminar</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="600">
      <v-card>
        <v-card-title>{{ form.id ? 'Editar' : 'Nuevo' }} Webhook</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-select v-model="form.event" :items="eventOptions" label="Evento" outlined dense :rules="[v => !!v || 'Requerido']" />
            <v-text-field v-model="form.url" label="URL" outlined dense :rules="[v => !!v || 'Requerido']" />
            <v-text-field v-model="form.secret" label="Secret (opcional)" outlined dense />
            <v-switch v-model="form.isActive" label="Activo" inset />
          </v-form>
          <div v-if="error" class="error--text mt-2">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="logsDialog" max-width="800">
      <v-card>
        <v-card-title>Logs - {{ selectedWebhook?.url }}</v-card-title>
        <v-card-text>
          <v-data-table :headers="logHeaders" :items="logs" dense>
            <template v-slot:item.responseStatus="{ item }">
              <v-chip x-small :color="item.responseStatus >= 200 && item.responseStatus < 300 ? 'success' : 'error'" dark label>
                {{ item.responseStatus || "ERR" }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="logsDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { webhooksService } from "../services/webhooks";

export default {
  name: "WebhooksView",
  data() {
    return {
      rows: [],
      logs: [],
      loading: false,
      dialog: false,
      logsDialog: false,
      valid: false,
      saving: false,
      error: "",
      selectedWebhook: null,
      headers: [
        { text: "Evento", value: "event" },
        { text: "URL", value: "url" },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      logHeaders: [
        { text: "Evento", value: "event" },
        { text: "Status", value: "responseStatus" },
        { text: "Duración (ms)", value: "durationMs" },
        { text: "Fecha", value: "createdAt" },
      ],
      eventOptions: [
        "product.created",
        "product.updated",
        "stock.updated",
        "customer.payment",
        "customer.debt_paid",
        "receipt.created",
        "price_list.assigned",
        "price_list.updated",
      ],
      form: { event: "", url: "", secret: "", isActive: true },
    };
  },
  created() {
    this.fetchRows();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && response.data.data !== undefined) return response.data.data;
      return response && response.data ? response.data : [];
    },
    async fetchRows() {
      this.loading = true;
      try {
        const res = await webhooksService.list();
        this.rows = this.unwrap(res) || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openForm(item) {
      this.error = "";
      if (item) {
        this.form = { ...item };
      } else {
        this.form = { event: "", url: "", secret: "", isActive: true };
      }
      this.dialog = true;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.error = "";
      try {
        if (this.form.id) {
          await webhooksService.update(this.form.id, this.form);
        } else {
          await webhooksService.create(this.form);
        }
        this.dialog = false;
        await this.fetchRows();
      } catch (e) {
        this.error = e.response?.data?.error || e.message || "Error al guardar";
      } finally {
        this.saving = false;
      }
    },
    async showLogs(item) {
      this.selectedWebhook = item;
      try {
        const res = await webhooksService.getLogs(item.id);
        this.logs = this.unwrap(res) || [];
        this.logsDialog = true;
      } catch (e) {
        console.error(e);
      }
    },
    async remove(item) {
      if (!confirm("¿Eliminar webhook?")) return;
      try {
        await webhooksService.remove(item.id);
        await this.fetchRows();
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.page-hero { background: var(--bg-card); border-radius: 16px; padding: 24px 28px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
.page-hero h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.02em; }
.page-hero p { margin: 0; color: var(--text-secondary); font-size: 14px; line-height: 1.5; }
.page-card { background: var(--bg-card); border-radius: 16px; padding: 8px 8px 4px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
</style>
